import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, map, tap, switchMap, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const authService = inject(AuthService);

  // Check if we're in local development mode
  const isLocalDev = !!(environment as any).isLocalDev || !(environment as any).auth?.enabled;

  if (isLocalDev) {
    console.log('🎭 Auth Guard - Local dev mode, allowing access to:', state.url);
    return of(true);
  }

  return authService.isAuthenticated$.pipe(
    map((authenticated: any) => {
      // Handle different response formats (boolean vs object)
      const isAuthenticated = typeof authenticated === 'boolean' ? authenticated : authenticated.isAuthenticated;

      if (isAuthenticated) {
        console.log('✅ Auth Guard - Access granted to:', state.url);
        return true;
      }

      console.log('❌ Auth Guard - Access denied to:', state.url, '- Redirecting to login');

      // Store the intended URL for redirecting after login (but not error pages)
      if (state.url !== '/unauthorized' && state.url !== '/login') {
        sessionStorage.setItem('redirectUrl', state.url);
      }

      // Redirect to login
      authService.login();
      return false;
    })
  );
};

/**
 * Guard for routes that should only be accessible to unauthenticated users
 * (e.g., login page)
 */
export const unauthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    map((authenticated: any) => {
      // Handle different response formats (boolean vs object)
      const isAuthenticated = typeof authenticated === 'boolean' ? authenticated : authenticated?.isAuthenticated;

      if (!isAuthenticated) {
        return true;
      }

      // If user is already authenticated, redirect to safe page
      router.navigate(['/address']);
      return false;
    })
  );
};

/**
 * Role-based guard
 */
export const roleGuard = (requiredRole: string): CanActivateFn => {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Prevent infinite loops - if already on unauthorized page, allow access
    if (state.url === '/unauthorized') {
      return of(true);
    }

    return authService.isAuthenticated$.pipe(
      switchMap(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          // Store the intended URL (but not error pages)
          if (state.url !== '/unauthorized' && state.url !== '/login') {
            sessionStorage.setItem('redirectUrl', state.url);
          }
          authService.login();
          return of(false);
        }

        return authService.hasRole(requiredRole).pipe(
          tap((hasRole) => {
            if (!hasRole) {
              console.warn('🚫 Role Guard - Access denied. Required role:', requiredRole);
              router.navigate(['/unauthorized']);
            }
          })
        );
      })
    );
  };
};
